<template>
  <section class="card cl-chart-card" :class="{ 'is-expanded': expanded }">
    <header class="cl-chart-toolbar">
      <div class="cl-chart-metrics" role="group" aria-label="Metric">
        <button
          v-for="metric in metrics"
          :key="metric.key"
          type="button"
          class="btn-sm-outline"
          :class="{ 'is-active': activeMetrics.includes(metric.key) }"
          :style="activeMetrics.includes(metric.key) ? { '--cl-metric-color': metric.color } : null"
          :title="metric.hint"
          @click="toggleMetric(metric.key)"
        >
          <i class="fa-solid" :class="metric.icon"></i>
          {{ metric.label }}
        </button>
      </div>

      <div class="cl-chart-controls">
        <AppDropdown v-model="breakdown" size="sm" label="Series" :options="breakdownOptions" />
        <AppDropdown v-model="bucketChoice" size="sm" label="Bucket" :options="bucketSelectOptions" />
        <AppDropdown v-model="smoothing" size="sm" label="Smoothing" :options="smoothingOptions" />
        <AppDropdown v-model="mode" size="sm" label="Shape" :options="shapeOptions" />
        <AppDropdown v-model="valueMode" size="sm" label="Values" align="right" :options="valueOptions" />

        <div class="cl-chart-buttons">
          <button
            class="btn-icon-outline"
            type="button"
            :disabled="!range"
            aria-label="Reset zoom"
            title="Reset zoom"
            @click="$emit('selectRange', null)"
          >
            <i class="fa-solid fa-magnifying-glass-minus"></i>
          </button>
          <button
            class="btn-icon-outline"
            type="button"
            :aria-label="expanded ? 'Smaller chart' : 'Taller chart'"
            :title="expanded ? 'Smaller chart' : 'Taller chart'"
            @click="expanded = !expanded"
          >
            <i class="fa-solid" :class="expanded ? 'fa-compress' : 'fa-expand'"></i>
          </button>
        </div>
      </div>
    </header>

    <p v-if="range" class="cl-chart-range">
      <i class="fa-solid fa-crop-simple"></i>
      {{ rangeLabel }}
      <button class="btn-sm-outline" type="button" @click="$emit('selectRange', null)">Reset zoom</button>
    </p>

    <CombatLogTimeChart
      :series="chartSeries"
      :bucket-ms="effectiveBucketMs"
      :start-index="startIndex"
      :height="chartHeight"
      :mode="mode"
      :unit="unit"
      :highlight-key="highlightKey"
      @select-range="onSelectRange"
    />

    <footer class="cl-chart-legend">
      <button
        v-for="entry in legend"
        :key="entry.key"
        type="button"
        class="cl-legend-item"
        :class="{ 'is-off': hidden[entry.key] }"
        @click="toggleSeries(entry.key)"
        @mouseenter="highlightKey = entry.key"
        @mouseleave="highlightKey = ''"
      >
        <i class="cl-chart-swatch" :style="{ background: entry.color }"></i>
        <span>{{ entry.label }}</span>
        <b>{{ format(entry.total) }}</b>
      </button>
      <span v-if="!legend.length" class="muted-line">Nothing to plot for this selection.</span>
      <span class="cl-chart-hint">Drag on the chart to zoom, double-click to reset.</span>
    </footer>
  </section>
</template>

<script>
import AppDropdown from '../AppDropdown.vue'
import CombatLogTimeChart from './CombatLogTimeChart.vue'
import { formatCompact, formatDuration } from '../../services/combatLog/format'

const MAX_POINTS = 420

const METRICS = [
  { key: 'damage', label: 'Damage', icon: 'fa-burst', color: 'var(--cl-damage)', hint: 'Damage done by the group' },
  { key: 'healing', label: 'Healing', icon: 'fa-heart-pulse', color: 'var(--cl-healing)', hint: 'Effective healing' },
  { key: 'taken', label: 'Damage taken', icon: 'fa-shield-halved', color: 'var(--cl-taken)', hint: 'Damage the group took' },
]

function resample(values, factor, length) {
  if (factor <= 1) return values.slice(0, length)
  const out = new Array(Math.ceil(length / factor)).fill(0)
  for (let index = 0; index < length; index += 1) {
    out[Math.floor(index / factor)] += values[index] || 0
  }
  return out
}

function smooth(values, window) {
  if (!window || window < 2) return values
  const out = new Array(values.length).fill(0)
  const half = Math.floor(window / 2)
  for (let index = 0; index < values.length; index += 1) {
    let sum = 0
    let count = 0
    for (let offset = -half; offset <= half; offset += 1) {
      const at = index + offset
      if (at < 0 || at >= values.length) continue
      sum += values[at] || 0
      count += 1
    }
    out[index] = count ? sum / count : 0
  }
  return out
}

function runningTotal(values) {
  let total = 0
  return values.map((value) => {
    total += value || 0
    return total
  })
}

export default {
  name: 'CombatLogChartPanel',
  components: { AppDropdown, CombatLogTimeChart },
  props: {
    report: { type: Object, required: true },
    // Zoom range in base buckets, owned by the view so it survives a refresh.
    range: { type: Object, default: null },
    playerGuid: { type: String, default: '' },
  },
  emits: ['selectRange'],
  data() {
    return {
      metrics: METRICS,
      activeMetrics: ['damage'],
      breakdown: 'players',
      bucketChoice: 0,
      smoothing: 3,
      mode: 'line',
      valueMode: 'rate',
      expanded: false,
      hidden: {},
      highlightKey: '',
    }
  },
  computed: {
    baseBucketMs() {
      return this.report.timeline.bucketMs || 1000
    },
    length() {
      return this.report.timeline.length || 0
    },
    bucketOptions() {
      const base = Math.round(this.baseBucketMs / 1000) || 1
      return [1, 5, 10, 30, 60, 300].filter((seconds) => seconds >= base)
    },
    breakdownOptions() {
      return [
        { value: 'group', label: 'Group total', hint: 'One line per metric' },
        { value: 'players', label: 'Per player', hint: 'One line per group member' },
      ]
    },
    bucketSelectOptions() {
      return [
        { value: 0, label: `Auto (${Math.round(this.effectiveBucketMs / 1000)}s)`, hint: 'Fits the window to the plot' },
        ...this.bucketOptions.map((seconds) => ({ value: seconds, label: `${seconds}s` })),
      ]
    },
    smoothingOptions() {
      return [
        { value: 0, label: 'Off', hint: 'Raw buckets' },
        { value: 3, label: '3 buckets' },
        { value: 5, label: '5 buckets' },
        { value: 9, label: '9 buckets' },
        { value: 15, label: '15 buckets' },
      ]
    },
    shapeOptions() {
      return [
        { value: 'line', label: 'Lines' },
        { value: 'area', label: 'Area' },
        { value: 'stacked', label: 'Stacked', hint: 'Series add up' },
      ]
    },
    valueOptions() {
      return [
        { value: 'rate', label: 'Per second' },
        { value: 'bucket', label: 'Per bucket' },
        { value: 'cumulative', label: 'Cumulative' },
      ]
    },
    factor() {
      const chosen = this.bucketChoice
        ? Math.max(1, Math.round((this.bucketChoice * 1000) / this.baseBucketMs))
        : 1
      const visible = this.visibleLength
      // Never draw more points than the plot can show.
      const minimum = Math.ceil(visible / MAX_POINTS) || 1
      return Math.max(chosen, minimum)
    },
    effectiveBucketMs() {
      return this.baseBucketMs * this.factor
    },
    from() {
      return this.range ? Math.max(0, this.range.from) : 0
    },
    to() {
      return this.range ? Math.min(this.length - 1, this.range.to) : Math.max(0, this.length - 1)
    },
    visibleLength() {
      return Math.max(1, this.to - this.from + 1)
    },
    startIndex() {
      return Math.floor(this.from / this.factor)
    },
    unit() {
      if (this.valueMode === 'rate') return '/s'
      return ''
    },
    // guid -> {damage, healing, taken} for the players the chart may show.
    playerSeries() {
      return this.report.players.map((player) => ({
        key: player.guid,
        label: player.name,
        color: player.color || 'var(--primary)',
        source: this.report.timeline.series[player.guid] || {},
      }))
    },
    rawSeries() {
      const total = this.report.timeline.total
      const slice = (values) => (values || []).slice(this.from, this.to + 1)

      if (this.breakdown === 'group' || this.activeMetrics.length > 1) {
        return this.activeMetrics.map((metric) => {
          const definition = METRICS.find((entry) => entry.key === metric)
          return {
            key: `group-${metric}`,
            label: definition.label,
            color: definition.color,
            values: slice(total[metric]),
          }
        })
      }

      const metric = this.activeMetrics[0] || 'damage'
      return this.playerSeries
        .map((player) => ({
          key: player.key,
          label: player.label,
          color: player.color,
          values: slice(player.source[metric]),
        }))
        .filter((entry) => entry.values.some((value) => value > 0))
    },
    shapedSeries() {
      const length = this.visibleLength
      const seconds = this.effectiveBucketMs / 1000

      return this.rawSeries.map((entry) => {
        let values = resample(entry.values, this.factor, length)
        if (this.valueMode === 'cumulative') {
          values = runningTotal(values)
        } else {
          values = smooth(values, this.smoothing)
          if (this.valueMode === 'rate') values = values.map((value) => value / seconds)
        }
        return { ...entry, values, total: entry.values.reduce((sum, value) => sum + (value || 0), 0) }
      }).sort((left, right) => right.total - left.total)
    },
    chartSeries() {
      return this.shapedSeries.filter((entry) => !this.hidden[entry.key])
    },
    legend() {
      return this.shapedSeries.map((entry) => ({
        key: entry.key,
        label: entry.label,
        color: entry.color,
        total: entry.total,
      }))
    },
    chartHeight() {
      return this.expanded ? 560 : 320
    },
    rangeLabel() {
      const from = this.from * this.baseBucketMs
      const to = (this.to + 1) * this.baseBucketMs
      return `Zoomed to ${formatDuration(from)} - ${formatDuration(to)} (${formatDuration(to - from)})`
    },
  },
  watch: {
    // A new fight or player selection must not keep a stale legend state.
    'report.key'() {
      this.hidden = {}
    },
  },
  methods: {
    format(value) {
      return formatCompact(value)
    },
    toggleMetric(key) {
      const active = new Set(this.activeMetrics)
      if (active.has(key) && active.size > 1) active.delete(key)
      else active.add(key)
      this.activeMetrics = METRICS.map((metric) => metric.key).filter((metric) => active.has(metric))
      this.hidden = {}
    },
    toggleSeries(key) {
      this.hidden = { ...this.hidden, [key]: !this.hidden[key] }
    },
    onSelectRange(range) {
      if (!range) {
        this.$emit('selectRange', null)
        return
      }
      // The chart reports resampled buckets; the view stores base buckets.
      this.$emit('selectRange', {
        from: range.from * this.factor,
        to: Math.min(this.length - 1, (range.to + 1) * this.factor - 1),
      })
    },
  },
}
</script>
